package com.evoapps.evoshift.domain.usecase.shift

import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.WorkHoursDecision
import com.evoapps.evoshift.domain.model.WorkHoursReportException
import com.evoapps.evoshift.domain.repository.ShiftRepository

/**
 * SH-257: a manager (owner, financial or operational manager) confirms the hours of one shift or
 * of the day ("Potwierdź dzień"): each [WorkHoursDecision] goes through the reviewClock function,
 * which writes the actual times and the decision covering them (`actual.review`) and logs it, and
 * refuses a closed day. Then, as for the SH-236 actions ([ShiftWorkFollowUps]), the confirmed shifts
 * are repriced and the day's record recomputed (by those who see the money; skipped otherwise), so
 * the labour cost, the pay estimate, the timesheet and the exports follow.
 */
class ConfirmWorkHoursUseCase(
    private val shiftRepository: ShiftRepository,
    private val followUps: ShiftWorkFollowUps
) {
    /** The shifts confirmed, those refused (with why), the day's record and missing pay rates. */
    data class Outcome(
        val confirmed: List<String>,
        val failed: Map<String, Throwable>,
        val record: DailyRecord?,
        val missingPayRates: Boolean = false
    )

    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        date: String,
        decisions: List<WorkHoursDecision>
    ): Result<Outcome> = runCatching {
        require(decisions.isNotEmpty()) { "Nothing to confirm" }
        followUps.requireOpen(rosterId, date)
        val confirmed = mutableListOf<String>()
        val failed = linkedMapOf<String, Throwable>()
        // One after another: each is a transaction on the shift and a log entry.
        for (decision in decisions) {
            shiftRepository.reviewClock(decision.shiftId, decision.action, decision.start, decision.end).fold(
                onSuccess = { confirmed += decision.shiftId },
                onFailure = { e ->
                    if (e is kotlinx.coroutines.CancellationException) throw e
                    failed[decision.shiftId] = e
                }
            )
        }
        // Nothing went through: the first refusal is the answer (the screen words it).
        if (confirmed.isEmpty()) throw failed.values.first()
        val missingRates = followUps.reprice(confirmed)
        val record = followUps.recomputeDay(workplaceId, rosterId, date)
        Outcome(confirmed, failed, record, missingRates)
    }
}

/**
 * SH-257: the person enters the actual times of their own shift ("Wpisz godziny pracy"), where the
 * workplace lets the team (settings.workHours). The reportWorkHours function checks the window,
 * the shift's owner and that no manager confirmed them yet; the managers see them in Godziny pracy
 * as "Zgłoszone przez pracownika" until they confirm them. The cost follows once confirmed (a
 * financial manager reprices the day then); until then the shift counts as before.
 */
class ReportWorkHoursUseCase(private val shiftRepository: ShiftRepository) {
    suspend operator fun invoke(shiftId: String, start: String, end: String): Result<Unit> {
        if (end.isEmpty() || start.isEmpty() || end <= start) {
            return Result.failure(WorkHoursReportException(WorkHoursReportException.Reason.BAD_TIMES))
        }
        return shiftRepository.reportWorkHours(shiftId, start, end)
    }
}
